import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Flag, Shield, Trash2, EyeOff, ImageOff, Ban, FileText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { MediaPreview } from "@/components/MediaUploader";
import { VoteButtons } from "@/components/social/VoteButtons";
import { CommentsSection } from "@/components/social/CommentsSection";
import { toast } from "sonner";

function Avatar({ url, name, size = 32 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        return <img src={full} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.42 }}>
            {initial}
        </div>
    );
}

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
}

function TierBadge({ tier }) {
    if (tier === "research_council") return (
        <span className="inline-flex items-center gap-1" title="Research Council">
            <MembershipBadge tier="research_council" size="sm" />
            <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: "#e7c873" }}>Council</span>
        </span>
    );
    if (tier === "verified_witness") return (
        <span className="inline-flex items-center gap-1" title="Verified Witness">
            <MembershipBadge tier="verified_witness" size="sm" />
            <span className="font-mono text-[9px] uppercase tracking-widest opacity-80">Witness</span>
        </span>
    );
    return null;
}

/**
 * PostCard — a Story / Live Drop post on the wall, homepage or profile.
 * Shows author + badge + timestamp, body, media, linked case, vote controls,
 * replies, report button and moderator tools.
 */
export function PostCard({ post, onRemoved, compact = false }) {
    const { user } = useAuth();
    const [showReplies, setShowReplies] = useState(false);
    const [reporting, setReporting] = useState(false);
    const [reason, setReason] = useState("");
    const [removed, setRemoved] = useState(false);
    const [media, setMedia] = useState(post.media);
    const isModerator = user && (user.role === "admin" || user.community_moderation_access);
    const isMine = user && user.id === post.user_id;

    if (removed) return null;

    async function submitReport() {
        if (!reason.trim()) return;
        try {
            await api.post("/social/report", { target_type: "post", target_id: post.id, reason: reason.trim() });
            toast.success("Report filed. A moderator will review it");
            setReporting(false);
            setReason("");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    }

    async function modAction(action) {
        try {
            if (action === "hide") {
                await api.post(`/social/posts/${post.id}/hide`);
                toast.success("Post hidden");
                setRemoved(true);
                onRemoved?.(post.id);
            } else if (action === "remove-media") {
                await api.post(`/social/posts/${post.id}/remove-media`);
                toast.success("Media removed");
                setMedia(null);
            } else if (action === "delete") {
                await api.delete(`/social/posts/${post.id}`);
                toast.success("Post deleted");
                setRemoved(true);
                onRemoved?.(post.id);
            } else if (action === "suspend") {
                await api.post(`/social/admin/users/${post.user_id}/suspend`, { suspended: true });
                toast.success("User suspended");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    }

    const elevated = post.user_subscription_tier === "research_council";

    return (
        <article
            className={`ok-card ${compact ? "p-3" : "p-4"} ${elevated ? "border-l-2" : ""}`}
            style={elevated ? { borderLeftColor: "#e7c873", background: "rgba(231,200,115,0.04)" } : undefined}
            data-testid={`post-card-${post.id}`}
        >
            <div className="flex items-start gap-3">
                <Link to={`/u/${post.user_id}`}><Avatar url={post.user_avatar_url} name={post.user_name} size={compact ? 28 : 36} /></Link>
                <div className="flex-1 min-w-0">
                    <div className="text-xs flex items-center gap-2 flex-wrap">
                        <Link to={`/u/${post.user_id}`} className="font-mono uppercase tracking-wide hover:text-[var(--ok-accent)]" data-testid={`post-author-${post.id}`}>
                            {post.user_name || "Witness"}
                        </Link>
                        <TierBadge tier={post.user_subscription_tier} />
                        {post.from_followed && (
                            <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--ok-accent)] opacity-80">Following</span>
                        )}
                        <span className="opacity-50">· {timeAgo(post.created_at)}</span>
                    </div>
                    {post.body && (
                        <p className={`${compact ? "text-sm" : "text-[15px]"} mt-1.5 whitespace-pre-wrap break-words leading-relaxed`} data-testid={`post-body-${post.id}`}>
                            {post.body}
                        </p>
                    )}
                    {media && (
                        <div className="mt-2">
                            <MediaPreview media={media} />
                        </div>
                    )}
                    {post.case?.id && (
                        <Link to={`/encounters/${post.case.id}`} className="ok-tag inline-flex items-center gap-1 mt-2 hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid={`post-case-link-${post.id}`}>
                            <FileText className="w-3 h-3" /> {post.case.title || "Linked case"}
                        </Link>
                    )}

                    <div className="mt-2.5 flex items-center gap-3 flex-wrap text-xs">
                        <VoteButtons
                            targetType="post"
                            targetId={post.id}
                            initialScore={post.score || 0}
                            initialMyVote={post.my_vote || 0}
                            layout="horizontal"
                            size="sm"
                        />
                        {compact ? (
                            <Link to="/community" className="inline-flex items-center gap-1 opacity-60 hover:opacity-100 font-mono" data-testid={`post-replies-link-${post.id}`}>
                                <MessageCircle className="w-3.5 h-3.5" /> {post.reply_count || 0}
                            </Link>
                        ) : (
                            <button onClick={() => setShowReplies((v) => !v)} className="inline-flex items-center gap-1 opacity-60 hover:opacity-100 font-mono" data-testid={`post-replies-btn-${post.id}`}>
                                <MessageCircle className="w-3.5 h-3.5" /> {post.reply_count || 0} {showReplies ? "· hide" : ""}
                            </button>
                        )}
                        {user && !isMine && (
                            <button onClick={() => setReporting((v) => !v)} className="inline-flex items-center gap-1 opacity-40 hover:opacity-100 hover:text-red-400 font-mono" data-testid={`post-report-btn-${post.id}`}>
                                <Flag className="w-3 h-3" /> Report
                            </button>
                        )}
                        {(isMine || isModerator) && (
                            <button onClick={() => modAction("delete")} className="inline-flex items-center gap-1 opacity-40 hover:opacity-100 hover:text-red-400 font-mono" data-testid={`post-delete-btn-${post.id}`}>
                                <Trash2 className="w-3 h-3" /> Delete
                            </button>
                        )}
                        {isModerator && !isMine && (
                            <span className="inline-flex items-center gap-2 ml-auto" data-testid={`post-mod-tools-${post.id}`}>
                                <Shield className="w-3 h-3 opacity-50" />
                                <button onClick={() => modAction("hide")} title="Hide post" className="opacity-50 hover:opacity-100" data-testid={`post-hide-btn-${post.id}`}>
                                    <EyeOff className="w-3.5 h-3.5" />
                                </button>
                                {media && (
                                    <button onClick={() => modAction("remove-media")} title="Remove media" className="opacity-50 hover:opacity-100" data-testid={`post-remove-media-btn-${post.id}`}>
                                        <ImageOff className="w-3.5 h-3.5" />
                                    </button>
                                )}
                                <button onClick={() => modAction("suspend")} title="Suspend user" className="opacity-50 hover:opacity-100 hover:text-red-400" data-testid={`post-suspend-btn-${post.id}`}>
                                    <Ban className="w-3.5 h-3.5" />
                                </button>
                            </span>
                        )}
                    </div>

                    {reporting && (
                        <div className="mt-2 flex items-center gap-2" data-testid={`post-report-form-${post.id}`}>
                            <input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Why are you reporting this?"
                                className="ok-input flex-1 text-xs"
                                maxLength={1000}
                                data-testid={`post-report-input-${post.id}`}
                            />
                            <button onClick={submitReport} disabled={!reason.trim()} className="ok-btn-outline text-xs" data-testid={`post-report-submit-${post.id}`}>
                                File report
                            </button>
                        </div>
                    )}

                    {showReplies && !compact && (
                        <div className="mt-1 -mb-4">
                            <CommentsSection targetType="post" targetId={post.id} />
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
}
